import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker, PaginatedResponse, acceptInvitation } from '../api/worker';
import { useCurrentUser } from '../contexts/AuthContext';
import { PaginationControls } from '../components/ui/PaginationControls';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Mail, Check, X, Building2, Clock } from 'lucide-react';
import { safeFormat } from '../lib/utils';
import toast from 'react-hot-toast';

export const MyInvitationsPage = () => {
  const [page, setPage] = useState(0);
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  const { data: invitationsData, isLoading } = useQuery({
    queryKey: ['my-invitations', page],
    queryFn: async () => {
      const response = await apiWorker.get<PaginatedResponse<any>>(`/api/invitations/my?page=${page}&size=10`);
      return response.data;
    }
  });

  const acceptMutation = useMutation({
    mutationFn: (invite: any) => {
      const token = invite.token;
      const id = invite.id;
      const firstName = user?.firstName || '';
      const lastName = user?.lastName || '';
      return acceptInvitation({ token, id, firstName, lastName });
    },
    onSuccess: () => {
      toast.success('Invitation accepted!');
      queryClient.invalidateQueries({ queryKey: ['my-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-live'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-mock'] });
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      // Invalidate node lists to reflect the new membership
      queryClient.invalidateQueries({ queryKey: ['node-config-full'] }); 
    },
    onError: () => toast.error('Failed to accept invitation.')
  });

  const declineMutation = useMutation({
    mutationFn: (id: string) => apiWorker.post(`/api/invitations/${id}/decline`),
    onSuccess: () => {
      toast.success('Invitation declined.');
      queryClient.invalidateQueries({ queryKey: ['my-invitations'] });
    },
    onError: () => toast.error('Failed to decline invitation.')
  });

  const invitations = invitationsData?.content || [];

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
          <Mail className="text-brand-primary" size={28} />
          My Invitations
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Review and manage your pending organization invites.
        </p>
      </div>

      <Card className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Spinner size="lg" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">Loading your invitations...</p>
          </div>
        ) : invitations.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {invitations.map((invite: any) => (
              <div key={invite.id} className="p-6 hover:bg-white transition-colors dark:bg-slate-950">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-none flex items-center justify-center shrink-0">
                      <Building2 size={24} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900 dark:text-gray-100 text-lg">{invite.nodeName || 'Organization'}</h3>
                        <Badge variant={invite.status === 'PENDING' ? 'amber' : 'gray'} className="text-[10px] uppercase">
                          {invite.status || 'PENDING'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1 font-medium text-gray-700">
                          Role: <Badge variant="blue" className="px-1.5 py-0 text-[10px] ml-1">{invite.role}</Badge>
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-gray-400 font-mono">
                          <Clock size={14} />
                          {safeFormat(invite.createdAt || new Date(), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {invite.status === 'PENDING' && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="secondary" className="bg-red-50 text-red-600 border-red-100 hover:bg-red-100 px-4" onClick={() => declineMutation.mutate(invite.id)} isLoading={declineMutation.isPending && declineMutation.variables === invite.id} disabled={acceptMutation.isPending}>
                        <X size={16} className="mr-1" /> Decline
                      </Button>
                      <Button variant="primary" className="bg-green-600 hover:bg-green-700 border-none px-6" onClick={() => acceptMutation.mutate(invite)} isLoading={acceptMutation.isPending && ((acceptMutation.variables as any)?.id === invite.id || (acceptMutation.variables as any)?.token === invite.token)} disabled={declineMutation.isPending}>
                        <Check size={16} className="mr-1" /> Accept
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {invitationsData && invitationsData.totalPages > 1 && (
              <div className="p-6">
                <PaginationControls currentPage={invitationsData.number} totalPages={invitationsData.totalPages} isFirst={invitationsData.first} isLast={invitationsData.last} onPageChange={setPage} totalElements={invitationsData.totalElements} />
              </div>
            )}
          </div>
        ) : (
          <div className="py-20 text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-white rounded-none flex items-center justify-center mb-4 dark:bg-slate-950">
              <Mail size={32} className="text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">No pending invitations</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">You don't have any pending invites to join an organization.</p>
          </div>
        )}
      </Card>
    </div>
  );
};